export type Format = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const SCIENTIFIC_ABOVE = 1e15;
const SCIENTIFIC_BELOW = 1e-6;

/** Choices for the "decimal places" control. */
export const DECIMAL_PLACES = ["0", "1", "2", "3", "4", "6", "8"] as const;
export type DecimalPlaces = (typeof DECIMAL_PLACES)[number];

/**
 * A result rounded to the reader's chosen decimal places. A value that would round to
 * zero keeps four significant digits instead, so a small area is never shown as 0, and
 * a very large or very small one switches to scientific notation.
 */
export function formatMeasure(
  fmt: Format,
  value: number,
  places: number
): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (value === 0) {
    return fmt(0);
  }
  const magnitude = Math.abs(value);
  if (magnitude >= SCIENTIFIC_ABOVE || magnitude < SCIENTIFIC_BELOW) {
    return fmt(value, { notation: "scientific", maximumFractionDigits: 4 });
  }
  const fixed = fmt(value, { maximumFractionDigits: places });
  if (Number(value.toFixed(places)) === 0) {
    return fmt(value, { maximumSignificantDigits: 4 });
  }
  return fixed;
}

/** A conversion-table value: seven significant digits, scientific at the extremes. */
export function formatTableValue(fmt: Format, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (value === 0) {
    return fmt(0);
  }
  const magnitude = Math.abs(value);
  if (magnitude >= SCIENTIFIC_ABOVE || magnitude < SCIENTIFIC_BELOW) {
    return fmt(value, { notation: "scientific", maximumFractionDigits: 4 });
  }
  return fmt(value, { maximumSignificantDigits: 7 });
}
